import { TownCountyCell } from '@/features/jobs/components/town-county-cell'
import { formatDateRaised } from '@/features/jobs/lib/format-date-raised'
import { formatJobType } from '@/features/jobs/lib/format-job-type'
import { DrilldownLink } from '@/features/reports/components/drilldown-link'
import type { Job, JobType } from '@/features/jobs/types/job'
import type { JobsReportBaseFilters } from '@/features/reports/types/jobs-report'
import type { ListTableColumn } from '@/shared/components'

type JobsReportRow = Job & {
  assigned_partner_id: number
  assigned_partner_name: string
}

type BuildJobsReportBaseColumnsArgs<TJob extends JobsReportRow> = {
  filters: JobsReportBaseFilters
  partnerTo: (assignedPartnerId: number) => string
  jobTypeTo: (jobType: JobType) => string
  primaryColumns: ListTableColumn<TJob>[]
}

export const buildJobsReportBaseColumns = <TJob extends JobsReportRow>({
  filters,
  partnerTo,
  jobTypeTo,
  primaryColumns,
}: BuildJobsReportBaseColumnsArgs<TJob>): ListTableColumn<TJob>[] => [
  {
    id: 'date_raised',
    header: 'Date Raised',
    cell: (job) => formatDateRaised(job.date_raised),
  },
  {
    id: 'assigned_partner_name',
    header: 'SiteWorks Partner',
    cell: (job) => (
      <DrilldownLink
        label={job.assigned_partner_name}
        active={filters.assigned_partner_id === job.assigned_partner_id}
        to={partnerTo(job.assigned_partner_id)}
      />
    ),
  },
  {
    id: 'job_type',
    header: 'Job Type',
    cell: (job) => (
      <DrilldownLink
        label={formatJobType(job.job_type)}
        active={filters.job_type === job.job_type}
        to={jobTypeTo(job.job_type)}
      />
    ),
  },
  {
    id: 'location_id',
    header: 'Location ID',
    cell: (job) => (
      <span className="font-bold">{job.location.location_id}</span>
    ),
  },
  {
    id: 'road_name',
    header: 'Road Name',
    cell: (job) => job.location.road_name ?? '',
  },
  {
    id: 'town_county',
    header: 'City/Town \n County',
    cell: (job) => <TownCountyCell location={job.location} />,
  },
  {
    id: 'asset_location',
    header: 'Asset Location',
    cell: (job) => job.location.asset_location ?? '',
  },
  ...primaryColumns,
]
